/**
 * Symbol Position Selector Component
 * Allows user to add symbols to positions
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import api from '../../services/api';

const SymbolPositionSelector = ({ availableSymbols, availablePositions, onAddSymbol, onSymbolSelect }) => {
  const { userSession } = useSelector((state) => state.auth);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [symbolSuggestions, setSymbolSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingSymbols, setSearchingSymbols] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);

  // Calculate dropdown position
  useEffect(() => {
    if (showSuggestions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX,
        width: 320
      });
    }
  }, [showSuggestions]);

  const handleAdd = () => {
    if (!selectedSymbol || !selectedPosition) {
      alert('Please select both symbol and position');
      return;
    }

    onAddSymbol(selectedSymbol, parseInt(selectedPosition));
    setSelectedSymbol('');
    setSelectedPosition('');
    setSearchTerm('');
    setSymbolSuggestions([]);
    setShowSuggestions(false);
  };

  // Search symbols from API
  const searchSymbols = async (query) => {
    if (query.length < 3) {
      setSymbolSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setSearchingSymbols(true);
      const response = await api.get('/symbols/search', {
        params: {
          q: query,
          exchange: 'NSE',
          limit: 20
        },
        headers: { Authorization: `Bearer ${userSession}` }
      });

      if (response.data.success) {
        setSymbolSuggestions(response.data.data || []);
        setShowSuggestions(true);
      }
    } catch (err) {
      console.error('Symbol search error:', err);
    } finally {
      setSearchingSymbols(false);
    }
  };

  const handleSearchChange = (value) => {
    const upperValue = value.toUpperCase();
    setSearchTerm(upperValue);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    if (upperValue.length >= 3) {
      const timeout = setTimeout(() => {
        searchSymbols(upperValue);
      }, 300);
      setSearchTimeout(timeout);
    } else {
      setSymbolSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSymbolSuggestion = (symbol) => {
    // Extract base symbol name without exchange suffix
    let symbolName = symbol.tradingSymbol || symbol.symbol || symbol;
    
    // Remove exchange suffixes like -EQ, -BE, etc.
    symbolName = symbolName.replace(/-(EQ|BE|BZ|SM|ST|SL|IL|IT)$/i, '').trim();
    
    setSelectedSymbol(symbolName);
    setSearchTerm(symbolName);
    setShowSuggestions(false);
    setSymbolSuggestions([]);

    // Immediately trigger position selection popup
    if (onSymbolSelect) {
      onSymbolSelect(symbolName);
      // Clear search after selecting
      setTimeout(() => {
        setSearchTerm('');
        setSelectedSymbol('');
      }, 100);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap relative z-[10001]">
      <div className="flex items-center gap-1">
        <label className="text-white font-semibold text-xs">Symbol:</label>
        <div className="relative z-[10002]">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search symbol..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => {
              if (searchTerm.length >= 3 && symbolSuggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => {
              setTimeout(() => setShowSuggestions(false), 200);
            }}
            className="bg-white/20 text-white px-2 py-1 rounded-lg border border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 w-40 placeholder-white/50 text-xs"
            autoComplete="off"
          />
          {searchingSymbols && (
            <div className="absolute right-2 top-1.5 text-blue-400">
              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          )}
          
          {/* Autocomplete Dropdown - Rendered via Portal */}
          {showSuggestions && symbolSuggestions.length > 0 && createPortal(
            <div 
              className="fixed bg-gray-800 border-2 border-blue-400 rounded-lg shadow-2xl max-h-64 overflow-y-auto z-[10003]"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: '320px'
              }}
            >
              <div className="p-2 bg-gradient-to-r from-blue-900 to-purple-900 border-b border-blue-600">
                <p className="text-xs text-white font-semibold">
                  💡 Found {symbolSuggestions.length} symbols - Click to select
                </p>
              </div>
              {symbolSuggestions.map((symbol, index) => (
                <div
                  key={index}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectSymbolSuggestion(symbol);
                  }}
                  className="px-3 py-2 hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 cursor-pointer border-b border-gray-700 transition-all duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-white text-sm">
                        {symbol.tradingSymbol}
                      </div>
                      <div className="text-xs text-gray-300 mt-0.5">
                        {symbol.instrumentName}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full font-medium">
                          {symbol.exchange}
                        </span>
                        {symbol.instrumentType && (
                          <span className="text-xs bg-purple-500 text-white px-2 py-0.5 rounded-full font-medium">
                            {symbol.instrumentType}
                          </span>
                        )}
                        {symbol.lotSize && (
                          <span className="text-xs text-gray-400">
                            Lot: {symbol.lotSize}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>,
            document.body
          )}
          
          {searchTerm.length > 0 && searchTerm.length < 3 && createPortal(
            <div 
              className="fixed bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-2 z-[10003]"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: '320px'
              }}
            >
              <p className="text-xs text-white/70">
                💡 Type at least 3 characters to search
              </p>
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
};

export default SymbolPositionSelector;
